"use client";

import { useSuspenseQuery } from "@tanstack/react-query";
import { type ReactNode } from "react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#/components/ui/card";
import { FieldGroup } from "#/components/ui/field";
import { Skeleton } from "#/components/ui/skeleton";
import { playerProfileQueryOptions } from "#/lib/queries";

import { DisplayNameField } from "./-profile.displayNameSetting";
import { EndTurnConfirmationField } from "./-profile.endTurnConfirmationSetting";
import { FastAnimationsField } from "./-profile.fastAnimationsSetting";
import type { ProfileUserId } from "./-profile.types";

function SettingsCardShell({ children }: { children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Settings</CardTitle>
        <CardDescription>Manage your profile and game preferences.</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function SettingsCardSkeleton() {
  return (
    <SettingsCardShell>
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </SettingsCardShell>
  );
}

export function SettingsForm({ userId }: { userId: ProfileUserId }) {
  const { data: profile } = useSuspenseQuery(playerProfileQueryOptions(userId));

  return (
    <SettingsFormLoaded
      userId={userId}
      initialDisplayName={profile.displayName}
      initialEndTurnConfirmation={profile.endTurnConfirmation ?? true}
      initialFastAnimations={profile.fastAnimations ?? false}
    />
  );
}

function SettingsFormLoaded({
  userId,
  initialDisplayName,
  initialEndTurnConfirmation,
  initialFastAnimations,
}: {
  userId: ProfileUserId;
  initialDisplayName: string;
  initialEndTurnConfirmation: boolean;
  initialFastAnimations: boolean;
}) {
  return (
    <SettingsCardShell>
      <FieldGroup>
        <DisplayNameField userId={userId} initialDisplayName={initialDisplayName} />
        <EndTurnConfirmationField userId={userId} initialEnabled={initialEndTurnConfirmation} />
        <FastAnimationsField userId={userId} initialEnabled={initialFastAnimations} />
      </FieldGroup>
    </SettingsCardShell>
  );
}
